import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Copy, Check } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { invitationService } from '../../services/invitationService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

const inviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const InvitePartner = () => {
  const { userProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: zodResolver(inviteSchema),
  });

  const onSubmit = async (data) => {
    if (!userProfile?.familyId) {
      setError('No family found. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await invitationService.sendInvitation(
        userProfile.familyId,
        userProfile.uid,
        data.email
      );

      if (result.success) {
        const link = invitationService.createInvitationLink(result.token);
        setInvitationLink(link);
        setInvitationSent(true);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  if (invitationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Invitation Sent!</h1>
            <p className="mt-2 text-gray-600">Your partner can now join your family account</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Invitation Details</CardTitle>
              <CardDescription>
                We've sent an invitation to {getValues('email')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invitation Link
                  </label>
                  <div className="flex space-x-2">
                    <Input
                      value={invitationLink}
                      readOnly
                      className="flex-1 text-sm"
                    />
                    <Button
                      onClick={copyToClipboard}
                      variant="outline"
                      size="sm"
                      className="flex-shrink-0"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    This link will expire in 7 days
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Next steps:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-1 space-y-1">
                    <li>• Your partner will receive an email with the invitation</li>
                    <li>• They can click the link or use the invitation code</li>
                    <li>• Once accepted, you'll both have access to shared finances</li>
                  </ul>
                </div>

                <div className="flex space-x-3">
                  <Link to="/" className="flex-1">
                    <Button className="w-full">
                      Go to Dashboard
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setInvitationSent(false);
                      setInvitationLink('');
                      setError('');
                    }}
                    className="flex-1"
                  >
                    Send Another
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Invite Your Partner</h1>
          <p className="mt-2 text-gray-600">Share your family finances together</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Send Invitation</CardTitle>
            <CardDescription>
              Enter your partner's email address to send them an invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Partner's Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="partner@example.com"
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    {...register('email')}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-sm text-gray-700">
                  <strong>What happens next?</strong>
                </p>
                <ul className="text-sm text-gray-600 mt-1 space-y-1">
                  <li>• Your partner will receive an email invitation</li>
                  <li>• They can create an account or sign in</li>
                  <li>• Once accepted, you'll share the same family account</li>
                  <li>• All expenses and budgets will be synced between you</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </Button>
                <Link to="/">
                  <Button variant="outline" className="flex-1">
                    Skip for Now
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvitePartner;
